<template>
  <div class="flex min-h-screen flex-col bg-neutral-50">
    <Head :title="$t('registration.page_title')" />

    <DynamicBlock v-if="header" :block="header" />

    <main class="mx-auto w-full max-w-[1200px] flex-1 px-4 py-8 sm:px-6 lg:px-0">
      <!-- Breadcrumbs (jak w profilu lekarza) -->
      <nav class="mb-8 flex min-w-0 flex-wrap items-center gap-1 text-sm" aria-label="Breadcrumb">
        <Link :href="homeHref" class="shrink-0 text-neutral-700 hover:text-[#2ea8cf]">
          {{ $t('breadcrumbs.home') }}
        </Link>
        <span class="shrink-0 text-neutral-300" aria-hidden="true">/</span>
        <Link :href="specialistsIndexHref" class="shrink-0 text-neutral-700 hover:text-[#2ea8cf]">
          {{ $t('breadcrumbs.specialists') }}
        </Link>
        <span class="shrink-0 text-neutral-300" aria-hidden="true">/</span>
        <Link :href="specialistHref" class="shrink-0 text-neutral-700 hover:text-[#2ea8cf]">
          {{ specialist.full_name }}
        </Link>
        <span class="shrink-0 text-neutral-300" aria-hidden="true">/</span>
        <span class="min-w-0 break-words text-[#2ea8cf]">{{ $t('registration.page_title') }}</span>
      </nav>

      <div class="grid min-w-0 grid-cols-1 gap-6 lg:grid-cols-3 lg:items-start lg:gap-8">
        <!-- Lewa kolumna: formularz (po sukcesie idziemy na /wizyty/:id) -->
        <div class="order-2 min-w-0 lg:order-1 lg:col-span-2">
          <form
            class="flex flex-col gap-6 rounded-xl bg-white p-6 shadow-[0px_6px_15px_-2px_rgba(16,24,40,0.08)]"
            novalidate
            @submit.prevent="submit"
          >
            <h1 class="text-2xl font-bold tracking-[-0.02em] lg:text-[32px] lg:leading-10">
              {{ $t('registration.heading') }}
            </h1>

            <div
              v-if="errorMessages.length"
              ref="errorAlert"
              class="flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 p-4 text-sm leading-5 text-red-700"
              role="alert"
              tabindex="-1"
            >
              <svg class="mt-0.5 size-5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01M10.29 3.86 1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
              </svg>
              <ul v-if="errorMessages.length > 1" class="list-disc space-y-1 pl-4">
                <li v-for="(message, index) in errorMessages" :key="index">{{ message }}</li>
              </ul>
              <span v-else>{{ errorMessages[0] }}</span>
            </div>

            <!-- Imię + nazwisko -->
            <div class="grid grid-cols-1 gap-6 sm:grid-cols-2">
              <div class="flex flex-col gap-2">
                <label :class="labelClass" for="patient_name">{{ $t('registration.first_name') }}</label>
                <input
                  id="patient_name"
                  v-model="form.patient_name"
                  type="text"
                  :class="[fieldClass, inputClass, fieldErrorClass('patient_name')]"
                  :aria-invalid="!!fieldErrors.patient_name"
                  autocomplete="given-name"
                  @input="clearFieldError('patient_name')"
                >
              </div>
              <div class="flex flex-col gap-2">
                <label :class="labelClass" for="patient_lastname">{{ $t('registration.last_name') }}</label>
                <input
                  id="patient_lastname"
                  v-model="form.patient_lastname"
                  type="text"
                  :class="[fieldClass, inputClass, fieldErrorClass('patient_lastname')]"
                  :aria-invalid="!!fieldErrors.patient_lastname"
                  autocomplete="family-name"
                  @input="clearFieldError('patient_lastname')"
                >
              </div>
            </div>

            <!-- E-mail -->
            <div class="flex flex-col gap-2">
              <label :class="labelClass" for="email">{{ $t('registration.email') }}</label>
              <input
                id="email"
                v-model.trim="form.email"
                type="email"
                :class="[fieldClass, inputClass, fieldErrorClass('email')]"
                :aria-invalid="!!fieldErrors.email"
                autocomplete="email"
                @input="clearFieldError('email')"
              >
            </div>

            <!-- Telefon -->
            <div class="flex flex-col gap-2">
              <label :class="labelClass" for="phone">{{ $t('registration.phone') }}</label>
              <div class="flex">
                <span
                  :class="[
                    'flex h-12 shrink-0 items-center rounded-l-lg border border-r-0 bg-neutral-100 px-3 text-base leading-[18px] text-neutral-900',
                    fieldErrors.phone ? 'border-red-500' : 'border-neutral-300',
                  ]"
                >
                  {{ form.phone_direction }}
                </span>
                <input
                  id="phone"
                  v-model.trim="form.phone"
                  type="tel"
                  :class="[fieldClass, inputClass, 'rounded-l-none', fieldErrorClass('phone')]"
                  :aria-invalid="!!fieldErrors.phone"
                  autocomplete="tel-national"
                  @input="clearFieldError('phone')"
                >
              </div>
              <p class="text-sm leading-4 text-neutral-500">
                {{ $t('registration.phone_note') }}
              </p>
            </div>

            <!-- PESEL -->
            <div class="flex flex-col gap-2">
              <label :class="labelClass" for="patient_pesel">{{ $t('registration.pesel') }}</label>
              <input
                id="patient_pesel"
                v-model.trim="form.patient_pesel"
                type="text"
                inputmode="numeric"
                :class="[fieldClass, inputClass, fieldErrorClass('patient_pesel')]"
                :aria-invalid="!!fieldErrors.patient_pesel"
                @input="clearFieldError('patient_pesel')"
              >
            </div>

            <!-- Kod pocztowy + miasto -->
            <div class="grid grid-cols-1 gap-6 sm:grid-cols-[128px_1fr]">
              <div class="flex flex-col gap-2">
                <label :class="labelClass" for="patient_zip">{{ $t('registration.zip') }}</label>
                <input
                  id="patient_zip"
                  v-model.trim="form.patient_zip"
                  type="text"
                  placeholder="XX-XXX"
                  :class="[fieldClass, inputClass, fieldErrorClass('patient_zip')]"
                  :aria-invalid="!!fieldErrors.patient_zip"
                  autocomplete="postal-code"
                  @input="clearFieldError('patient_zip')"
                >
              </div>
              <div class="flex flex-col gap-2">
                <label :class="labelClass" for="patient_city">{{ $t('registration.city') }}</label>
                <input
                  id="patient_city"
                  v-model.trim="form.patient_city"
                  type="text"
                  :class="[fieldClass, inputClass]"
                  autocomplete="address-level2"
                >
              </div>
            </div>

            <!-- Ulica + numer domu + numer lokalu -->
            <div class="grid grid-cols-1 gap-6 sm:grid-cols-[1fr_128px_128px]">
              <div class="flex flex-col gap-2">
                <label :class="labelClass" for="patient_street">{{ $t('registration.street') }}</label>
                <input
                  id="patient_street"
                  v-model.trim="form.patient_street"
                  type="text"
                  :class="[fieldClass, inputClass]"
                  autocomplete="address-line1"
                >
              </div>
              <div class="flex flex-col gap-2">
                <label :class="labelClass" for="patient_home_number">{{ $t('registration.home_number') }}</label>
                <input
                  id="patient_home_number"
                  v-model.trim="form.patient_home_number"
                  type="text"
                  :class="[fieldClass, inputClass]"
                >
              </div>
              <div class="flex flex-col gap-2">
                <label :class="labelClass" for="patient_home_place">{{ $t('registration.home_place') }}</label>
                <input
                  id="patient_home_place"
                  v-model.trim="form.patient_home_place"
                  type="text"
                  :class="[fieldClass, inputClass]"
                >
              </div>
            </div>

            <!-- Wiadomość -->
            <div class="flex flex-col gap-2">
              <label :class="labelClass" for="message">{{ $t('registration.message') }}</label>
              <textarea
                id="message"
                v-model.trim="form.message"
                rows="4"
                :placeholder="$t('registration.message_placeholder')"
                :class="[fieldClass, 'resize-y py-2']"
              />
            </div>

            <hr class="border-neutral-200">

            <ExpandableText
              :text="$t('registration.consent_note')"
              text-class="text-sm leading-5 text-neutral-500"
            />


            <!-- Regulamin -->
            <label class="flex items-center gap-3">
              <input
                v-model="form.reg"
                type="checkbox"
                class="size-[18px] shrink-0 rounded border-neutral-300 text-[#2ea8cf] focus:ring-[#2ea8cf]"
              >
              <span class="text-base leading-6 text-neutral-900">
                {{ $t('registration.accept_terms') }}
                <Link
                  href="/regulamin-strony"
                  class="font-semibold text-[#2ea8cf] hover:underline"
                  @click.stop
                >
                  {{ $t('registration.terms') }}
                </Link>
                *
              </span>
            </label>

            <button
              type="submit"
              :disabled="!canSubmit || submitting"
              class="flex cursor-pointer items-center justify-center gap-2 rounded-lg px-5 py-3.5 text-base font-semibold leading-[18px] text-white drop-shadow-[0px_2px_3px_rgba(16,24,40,0.06)] disabled:cursor-not-allowed disabled:opacity-50"
              style="background-image: linear-gradient(176.294deg, rgb(46, 168, 207) 0%, rgb(40, 182, 155) 100%)"
            >
              <Icon
                v-if="!submitting"
                name="check"
                class="size-5 shrink-0"
              />
              {{ submitButtonLabel }}
            </button>
          </form>
        </div>

        <!-- Prawa kolumna: podsumowanie wizyty -->
        <aside
          class="order-1 min-w-0 lg:order-2 lg:sticky transition-[top] duration-300 ease-out"
          :style="{ top: `${stickyPanelTop}px` }"
        >
          <BookingSummary
            :specialist="specialist"
            :service="service"
            :chosen-date="chosenDate"
            :facility="facility"
            :change-href="specialistHref"
          />
        </aside>
      </div>
    </main>

    <DynamicBlock v-if="footer" :block="footer" />
  </div>
</template>

<script>
import { Head, Link, router } from '@inertiajs/vue3';
import { validatePolish } from 'validate-polish';
import Layout from '@/Layouts/Main.vue';
import DynamicBlock from '@/Shared/DynamicBlock.vue';
import BookingSummary from '@/Shared/BookingSummary.vue';
import ExpandableText from '@/Shared/ExpandableText.vue';
import Icon from '@/Shared/Icon.vue';
import { saveBooking } from '@/utils/bookingStorage';
import { stickyPanelTop } from '@/utils/stickyMainHeader';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_DIGITS_RE = /^\d{9}$/;
const ZIP_RE = /^\d{2}-\d{3}$/;

export default {
  name: 'Registration',
  components: { Head, Link, DynamicBlock, BookingSummary, ExpandableText, Icon },
  layout: Layout,
  props: {
    specialist: { type: Object, required: true },
    service: { type: Object, default: null },
    chosenDate: { type: String, default: null },
    facility: { type: Object, default: null },
    paymentAmountPln: { type: Number, default: null },
    header: { type: Object, default: null },
    footer: { type: Object, default: null },
  },
  data() {
    return {
      submitting: false,
      result: null,
      fieldErrors: {
        patient_name: '',
        patient_lastname: '',
        email: '',
        phone: '',
        patient_pesel: '',
        patient_zip: '',
      },
      labelClass: 'text-sm font-medium leading-4 text-neutral-900',
      fieldClass:
        'w-full rounded-lg border border-neutral-300 bg-white px-3 text-base leading-[18px] text-neutral-900 shadow-[0px_1px_1px_rgba(16,24,40,0.04)] placeholder:text-neutral-400 focus:border-[#149dca] focus:outline-none focus:ring-1 focus:ring-[#149dca]',
      inputClass:
        'h-12',
      form: {
        patient_name: '',
        patient_lastname: '',
        email: '',
        phone: '',
        phone_direction: '+48',
        patient_pesel: '',
        patient_zip: '',
        patient_city: '',
        patient_street: '',
        patient_home_number: '',
        patient_home_place: '',
        message: '',
        reg: false,
      },
    };
  },
  computed: {
    stickyPanelTop() {
      return stickyPanelTop.value;
    },
    homeHref() {
      return '/';
    },
    specialistsIndexHref() {
      return this.$routes.specialists_index();
    },
    specialistHref() {
      return `${this.$routes.specialist(this.specialist.slug)}#umow-wizyte`;
    },
    canSubmit() {
      return this.form.reg && !!this.service && !!this.chosenDate;
    },
    requiresPrepayment() {
      return Boolean(this.specialist?.requires_prepayment);
    },
    submitButtonLabel() {
      if (this.submitting) {
        return this.requiresPrepayment
          ? this.$t('registration.submitting')
          : this.$t('registration.submitting_direct');
      }
      if (!this.requiresPrepayment) {
        return this.$t('registration.submit_direct');
      }
      const price = this.formatPrice(this.chargeAmount);
      if (price) {
        return this.$t('registration.submit_with_price', { price });
      }
      return this.$t('registration.submit');
    },
    chargeAmount() {
      if (this.paymentAmountPln != null) return this.paymentAmountPln;
      return this.service?.price ?? null;
    },
    errorMessages() {
      const messages = [];
      if (this.fieldErrors.patient_name) messages.push(this.fieldErrors.patient_name);
      if (this.fieldErrors.patient_lastname) messages.push(this.fieldErrors.patient_lastname);
      if (this.fieldErrors.email) messages.push(this.fieldErrors.email);
      if (this.fieldErrors.phone) messages.push(this.fieldErrors.phone);
      if (this.fieldErrors.patient_pesel) messages.push(this.fieldErrors.patient_pesel);
      if (this.fieldErrors.patient_zip) messages.push(this.fieldErrors.patient_zip);
      if (this.result?.status === 'error' && this.result.error) {
        messages.push(this.result.error);
      }
      return messages;
    },
  },
  methods: {
    fieldErrorClass(field) {
      return this.fieldErrors[field]
        ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
        : '';
    },
    clearFieldError(field) {
      if (this.fieldErrors[field]) {
        this.fieldErrors[field] = '';
      }
    },
    clearValidationErrors() {
      this.fieldErrors.patient_name = '';
      this.fieldErrors.patient_lastname = '';
      this.fieldErrors.email = '';
      this.fieldErrors.phone = '';
      this.fieldErrors.patient_pesel = '';
      this.fieldErrors.patient_zip = '';
    },
    validateContactFields() {
      this.clearValidationErrors();

      const firstName = this.form.patient_name.trim();
      const lastName = this.form.patient_lastname.trim();
      const email = this.form.email.trim();
      const phoneDigits = this.form.phone.replace(/\D/g, '');
      const pesel = this.form.patient_pesel.trim();
      const zip = this.form.patient_zip.trim();

      if (!firstName) {
        this.fieldErrors.patient_name = this.$t('registration.first_name_required');
      }
      if (!lastName) {
        this.fieldErrors.patient_lastname = this.$t('registration.last_name_required');
      }

      if (!email) {
        this.fieldErrors.email = this.$t('registration.email_required');
      } else if (!EMAIL_RE.test(email)) {
        this.fieldErrors.email = this.$t('registration.email_invalid');
      }

      if (!this.form.phone.trim()) {
        this.fieldErrors.phone = this.$t('registration.phone_required');
      } else if (!PHONE_DIGITS_RE.test(phoneDigits)) {
        this.fieldErrors.phone = this.$t('registration.phone_invalid');
      }

      // PESEL i kod pocztowy sa opcjonalne - walidujemy tylko, gdy podane.
      if (pesel && !validatePolish.pesel(pesel)) {
        this.fieldErrors.patient_pesel = this.$t('registration.pesel_invalid');
      }
      if (zip && !ZIP_RE.test(zip)) {
        this.fieldErrors.patient_zip = this.$t('registration.zip_invalid');
      }

      return !this.fieldErrors.patient_name
        && !this.fieldErrors.patient_lastname
        && !this.fieldErrors.email
        && !this.fieldErrors.phone
        && !this.fieldErrors.patient_pesel
        && !this.fieldErrors.patient_zip;
    },
    scrollToErrors() {
      this.$nextTick(() => {
        window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
        const alert = this.$refs.errorAlert;
        if (alert && typeof alert.focus === 'function') {
          alert.focus({ preventScroll: true });
        }
      });
    },
    async submit() {
      if (!this.canSubmit || this.submitting) return;

      this.result = null;

      if (!this.validateContactFields()) {
        this.scrollToErrors();
        return;
      }

      this.submitting = true;

      const payload = {
        specialist: this.specialist.slug,
        service: this.service?.remote_uuid,
        date: this.chosenDate,
        ...this.form,
        patient_name: this.form.patient_name.trim(),
        patient_lastname: this.form.patient_lastname.trim(),
        email: this.form.email.trim(),
        phone: this.form.phone.replace(/\D/g, ''),
        reg: this.form.reg ? 1 : 0,
        allow_payment: 1,
      };

      try {
        const response = await fetch('/api/rejestracja', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify(payload),
        });

        const data = await response.json().catch(() => ({}));

        console.log('[Rejestracja] odpowiedź:', data);

        this.handleResponse(response, data);
      } catch (error) {
        console.error('[Rejestracja] błąd sieci:', error);
        this.result = {
          status: 'error',
          error: this.$t('registration.network_error'),
        };
        this.scrollToErrors();
        this.submitting = false;
      }
    },
    handleResponse(response, data) {
      const errorMessage = (data && data.error) ? String(data.error).trim() : '';

      if (!response.ok || errorMessage) {
        this.persistBooking(data?.visit || null, 'error', errorMessage);
        this.result = {
          status: 'error',
          error: errorMessage || this.$t('registration.register_error'),
        };
        this.scrollToErrors();
        this.submitting = false;
        return;
      }

      const redirectUri = data?.redirectUri;
      const bookingUiStatus = redirectUri
        ? 'pending_payment'
        : (data?.status === 'registered' || data?.visit?.visitStatus === 'registered'
          ? 'success'
          : 'pending_payment');
      const booking = this.persistBooking(data?.visit || null, bookingUiStatus, '');
      const visitId = booking?.id || data?.visit?.id;

      if (!visitId) {
        this.result = {
          status: 'error',
          error: this.$t('registration.register_error'),
        };
        this.scrollToErrors();
        this.submitting = false;
        return;
      }

      // Always land on the visit page — payment redirect (if any) happens there.
      const query = redirectUri ? '?redirect_to_payment=1' : '';
      router.visit(`/wizyty/${encodeURIComponent(visitId)}${query}`);
    },
    formatPrice(price) {
      if (price == null || Number.isNaN(Number(price))) return '';
      return new Intl.NumberFormat('pl-PL', {
        style: 'currency',
        currency: 'PLN',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(Number(price)).replace(/\u00a0/g, ' ');
    },
    persistBooking(visitPayload, status, errorMessage) {
      try {
        const fromServer = visitPayload && typeof visitPayload === 'object' ? visitPayload : {};
        return saveBooking({
          ...fromServer,
          id: fromServer.id || undefined,
          status: status || fromServer.status || 'pending_payment',
          error: errorMessage || fromServer.error || '',
          specialistSlug: fromServer.specialistSlug || this.specialist.slug,
          specialistName: fromServer.specialistName || this.specialist.full_name,
          service: fromServer.service || (this.service
            ? {
              name: this.service.name,
              slug: this.service.slug,
              remote_uuid: this.service.remote_uuid,
              price: this.service.price,
              duration: this.service.duration,
            }
            : null),
          chosenDate: fromServer.chosenDate || this.chosenDate,
          facility: fromServer.facility || this.facility,
          patient: fromServer.patient || {
            name: this.form.patient_name.trim(),
            lastname: this.form.patient_lastname.trim(),
            email: this.form.email.trim(),
          },
          output: fromServer.output || null,
          raw: fromServer,
        });
      } catch {
        // cache to tylko wygoda — brak zapisu nie blokuje płatności
        return visitPayload || null;
      }
    },
  },
};
</script>
